// src/targets/TargetSpecificationModal.tsx
//
// BekalNanti — Milestone P3: Target Specifications Modal
// Structured, progressive multi-step workspace for mapping goal targets.
// Zero AI, zero external scraping, 100% deterministic persistence in bekalnanti_app.goal_targets.

import React, { useState, useEffect, useMemo } from 'react'
import { GoalRecord } from '../journey/types'
import {
  TargetSpecificationRecord,
  COUNTRY_OPTIONS,
  INSTITUTION_TYPES,
  evaluateTargetReadiness,
} from '../lib/targetSpecification'
import { upsertGoalTarget } from '../lib/targetService'
import { getGoalDomain } from '../lib/goalDomainRegistry'
import {
  X,
  Target,
  MapPin,
  Calendar,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Save,
} from 'lucide-react'

interface TargetSpecificationModalProps {
  isOpen: boolean
  goals: GoalRecord[]
  initialGoalId?: string | null
  onClose: () => void
  onRefreshJourney: () => void
}

export const TargetSpecificationModal: React.FC<TargetSpecificationModalProps> = ({
  isOpen,
  goals,
  initialGoalId,
  onClose,
  onRefreshJourney,
}) => {
  // Support all active goals (not archived)
  const activeGoals = useMemo(() => {
    return goals.filter((g) => g.status !== 'archived')
  }, [goals])

  // Active goal selection
  const [selectedGoalId, setSelectedGoalId] = useState<string>(() => {
    if (initialGoalId && activeGoals.some((g) => g.id === initialGoalId)) {
      return initialGoalId
    }
    return activeGoals[0]?.id || ''
  })

  useEffect(() => {
    if (initialGoalId && activeGoals.some((g) => g.id === initialGoalId)) {
      setSelectedGoalId(initialGoalId)
    } else if (activeGoals.length > 0) {
      setSelectedGoalId((current) =>
        activeGoals.some((g) => g.id === current) ? current : activeGoals[0]?.id || ''
      )
    }
  }, [initialGoalId, activeGoals])

  const activeGoal = useMemo(() => {
    return activeGoals.find((g) => g.id === selectedGoalId) || activeGoals[0] || null
  }, [activeGoals, selectedGoalId])

  // Domain specification for active goal
  const domain = useMemo(() => {
    return getGoalDomain(activeGoal?.goalType)
  }, [activeGoal?.goalType])

  // Stepper: 1: Jenjang & Bidang, 2: Lokasi & Institusi, 3: Waktu & Durasi, 4: Ringkasan
  const [currentStep, setCurrentStep] = useState<number>(1)

  // Local form state
  const [targetStage, setTargetStage] = useState<string>('s1')
  const [programName, setProgramName] = useState<string>('')
  const [targetCountry, setTargetCountry] = useState<string>('Indonesia')
  const [targetCity, setTargetCity] = useState<string>('')
  const [institutionName, setInstitutionName] = useState<string>('')
  const [institutionType, setInstitutionType] = useState<string>('public')
  const [targetYear, setTargetYear] = useState<number>(new Date().getFullYear() + 4)
  const [durationMonths, setDurationMonths] = useState<number>(48)
  const [targetCurrency, setTargetCurrency] = useState<string>('IDR')

  // UI state
  const [saving, setSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Sync state when active goal changes or modal opens
  useEffect(() => {
    if (activeGoal) {
      const existing = activeGoal.target
      if (existing) {
        setTargetStage(existing.targetStage || domain.stages[0]?.id || 's1')
        setProgramName(existing.programName || '')
        setTargetCountry(existing.targetCountry || 'Indonesia')
        setTargetCity(existing.targetCity || '')
        setInstitutionName(existing.institutionName || '')
        setInstitutionType(existing.institutionType || 'public')
        setTargetYear(existing.targetYear || (existing.targetDate ? new Date(existing.targetDate).getFullYear() : new Date().getFullYear() + 4))
        setDurationMonths(existing.durationMonths || domain.stages[0]?.defaultDurationMonths || 12)
        setTargetCurrency(existing.targetCurrency || 'IDR')
      } else {
        // Sensible initial defaults based on active goal domain
        const initialStageId = domain.stages[0]?.id || 's1'
        const initialDuration = domain.stages[0]?.defaultDurationMonths || 12
        setTargetStage(initialStageId)
        setProgramName('')
        setTargetCountry('Indonesia')
        setTargetCity('')
        setInstitutionName('')
        setInstitutionType('public')
        setTargetYear(new Date().getFullYear() + 2)
        setDurationMonths(initialDuration)
        setTargetCurrency('IDR')
      }
      setCurrentStep(1)
      setSaveSuccess(false)
      setErrorMessage(null)
    }
  }, [activeGoal, isOpen])

  // Automatically adjust duration when stage changes if duration is at default
  const handleStageSelect = (stageId: string) => {
    setTargetStage(stageId)
    const stageObj = domain.stages.find((s) => s.id === stageId)
    if (stageObj) {
      setDurationMonths(stageObj.defaultDurationMonths)
    }
  }

  // Handle Country selection with currency auto-adjustment
  const handleCountrySelect = (countryId: string) => {
    setTargetCountry(countryId)
    const opt = COUNTRY_OPTIONS.find((c) => c.id === countryId)
    if (opt) {
      setTargetCurrency(opt.currency)
    }
  }

  // Current draft target record for live evaluation
  const currentDraftTarget: TargetSpecificationRecord = useMemo(() => {
    return {
      id: activeGoal?.target?.id,
      goalId: activeGoal?.id || '',
      targetStage,
      programName,
      targetCountry,
      targetCity: targetCity.trim() || null,
      institutionName: domain.showInstitution ? (institutionName.trim() || null) : null,
      institutionType: domain.showInstitution ? (institutionType || null) : null,
      targetYear: Number(targetYear) || null,
      targetDate: targetYear ? `${targetYear}-08-01` : null,
      durationMonths: Number(durationMonths) || (domain.stages[0]?.defaultDurationMonths || 12),
      targetCurrency,
    }
  }, [
    activeGoal,
    targetStage,
    programName,
    targetCountry,
    targetCity,
    institutionName,
    institutionType,
    targetYear,
    durationMonths,
    targetCurrency,
    domain,
  ])

  // Live deterministic readiness evaluation
  const readiness = useMemo(() => {
    return evaluateTargetReadiness(currentDraftTarget, activeGoal?.goalType || 'education')
  }, [currentDraftTarget, activeGoal])

  // Persist current draft target to database
  const handleSave = async (shouldClose = false) => {
    if (!activeGoal) return
    setSaving(true)
    setErrorMessage(null)
    setSaveSuccess(false)

    try {
      await upsertGoalTarget(currentDraftTarget)
      setSaveSuccess(true)
      onRefreshJourney()

      if (shouldClose) {
        setTimeout(() => {
          onClose()
        }, 300)
      } else {
        setTimeout(() => setSaveSuccess(false), 2500)
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Gagal menyimpan spesifikasi sasaran.')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  if (activeGoals.length === 0 || !activeGoal) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-target-spec-title"
        className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs overflow-y-auto animate-in fade-in duration-200"
      >
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 w-full max-w-md p-6 text-center space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto">
            <AlertCircle size={24} />
          </div>
          <div>
            <h2 id="modal-target-spec-title" className="text-base font-bold text-slate-900">
              Belum Ada Sasaran Aktif
            </h2>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">
              Rumah tangga Anda belum memiliki sasaran rencana aktif. Tambahkan sasaran rencana terlebih dahulu pada dashboard.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 px-4 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition-colors cursor-pointer"
          >
            Tutup
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-target-spec-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs overflow-y-auto animate-in fade-in duration-200"
    >
      <div className="bg-white rounded-3xl shadow-xl border border-slate-200 w-full max-w-xl overflow-hidden flex flex-col my-auto max-h-[92vh]">
        {/* Top Header */}
        <div className="p-4 sm:p-5 border-b border-slate-100 bg-slate-50/60 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-teal-50 text-[#0EA5A4] flex items-center justify-center flex-shrink-0 shadow-2xs mt-0.5">
              <Target size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-200">
                  {domain.title}
                </span>
                {readiness.isComplete ? (
                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200 flex items-center gap-1">
                    <CheckCircle2 size={10} />
                    <span>Siap Dihitung</span>
                  </span>
                ) : (
                  <span className="text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                    Parsial ({readiness.completedFieldsCount}/4)
                  </span>
                )}
              </div>
              <h2
                id="modal-target-spec-title"
                className="text-base sm:text-lg font-bold text-slate-900 mt-1 leading-snug"
              >
                Spesifikasi Sasaran: {activeGoal.name}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Rincikan sasaran agar siap dihitung pada tahap estimasi biaya &amp; pendanaan.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Tutup modal"
            className="p-1.5 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Goal Selector (if user has multiple active goals) */}
        {activeGoals.length > 1 && (
          <div className="px-5 py-2.5 bg-slate-100/60 border-b border-slate-200/70 flex items-center gap-2 overflow-x-auto text-xs">
            <span className="text-slate-400 text-[11px] font-medium flex-shrink-0">
              Pilih Sasaran:
            </span>
            <div className="flex items-center gap-1.5">
              {activeGoals.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setSelectedGoalId(g.id)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                    g.id === selectedGoalId
                      ? 'bg-white text-teal-800 shadow-2xs border border-teal-200'
                      : 'text-slate-600 hover:bg-slate-200/60'
                  }`}
                >
                  {g.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step Indicator Bar */}
        <div className="px-5 pt-3 pb-2 border-b border-slate-100">
          <div className="grid grid-cols-4 gap-1 sm:gap-2 text-center text-[11px]">
            <button
              type="button"
              onClick={() => setCurrentStep(1)}
              className={`pb-1.5 border-b-2 font-semibold transition-colors flex items-center justify-center gap-1 cursor-pointer ${
                currentStep === 1
                  ? 'border-[#0EA5A4] text-teal-700'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              <Target size={13} />
              <span className="hidden sm:inline">1. {domain.targetStageLabel.split(' ')[0]}</span>
              <span className="sm:hidden">1</span>
            </button>
            <button
              type="button"
              onClick={() => setCurrentStep(2)}
              className={`pb-1.5 border-b-2 font-semibold transition-colors flex items-center justify-center gap-1 cursor-pointer ${
                currentStep === 2
                  ? 'border-[#0EA5A4] text-teal-700'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              <MapPin size={13} />
              <span className="hidden sm:inline">2. Lokasi</span>
              <span className="sm:hidden">2</span>
            </button>
            <button
              type="button"
              onClick={() => setCurrentStep(3)}
              className={`pb-1.5 border-b-2 font-semibold transition-colors flex items-center justify-center gap-1 cursor-pointer ${
                currentStep === 3
                  ? 'border-[#0EA5A4] text-teal-700'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              <Calendar size={13} />
              <span className="hidden sm:inline">3. Waktu</span>
              <span className="sm:hidden">3</span>
            </button>
            <button
              type="button"
              onClick={() => setCurrentStep(4)}
              className={`pb-1.5 border-b-2 font-semibold transition-colors flex items-center justify-center gap-1 cursor-pointer ${
                currentStep === 4
                  ? 'border-[#0EA5A4] text-teal-700'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              <CheckCircle2 size={13} />
              <span className="hidden sm:inline">4. Kesiapan</span>
              <span className="sm:hidden">4</span>
            </button>
          </div>
        </div>

        {/* Step Body Content */}
        <div className="p-5 overflow-y-auto space-y-4 flex-1">
          {/* STEP 1: Jenjang / Tahap & Bidang */}
          {currentStep === 1 && (
            <div className="space-y-4 animate-in fade-in duration-150">
              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1.5">
                  {domain.targetStageLabel} <span className="text-rose-500">*</span>
                </label>
                <p className="text-[11px] text-slate-500 mb-2.5">
                  {domain.targetStagePlaceholder}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {domain.stages.map((s) => {
                    const isSelected = targetStage === s.id
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => handleStageSelect(s.id)}
                        className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-teal-50/80 border-[#0EA5A4] text-[#0F766E] shadow-2xs ring-1 ring-[#0EA5A4]'
                            : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <p className="font-bold text-xs">{s.label}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {s.defaultDurationMonths >= 12
                            ? `~${Math.round(s.defaultDurationMonths / 12)} tahun`
                            : `~${s.defaultDurationMonths} bulan`}
                        </p>
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="pt-2 border-t border-slate-100">
                <label className="block text-xs font-bold text-slate-800 mb-1">
                  {domain.programNameLabel} <span className="text-rose-500">*</span>
                </label>
                <p className="text-[11px] text-slate-500 mb-2">
                  Pilih opsi umum atau ketik pilihan spesifik secara bebas.
                </p>

                {/* Popular chips */}
                {domain.commonPrograms.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2.5">
                    {domain.commonPrograms.map((prog) => (
                      <button
                        key={prog}
                        type="button"
                        onClick={() => setProgramName(prog)}
                        className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all cursor-pointer ${
                          programName === prog
                            ? 'bg-teal-700 text-white font-semibold shadow-2xs'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200/80'
                        }`}
                      >
                        {prog}
                      </button>
                    ))}
                  </div>
                )}

                {/* Freeform input */}
                <input
                  type="text"
                  value={programName}
                  onChange={(e) => setProgramName(e.target.value)}
                  placeholder={domain.programNamePlaceholder}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0EA5A4] focus:border-transparent"
                />
              </div>
            </div>
          )}

          {/* STEP 2: Lokasi & Lingkup */}
          {currentStep === 2 && (
            <div className="space-y-4 animate-in fade-in duration-150">
              {domain.showInstitution ? (
                /* EDUCATION-SPECIFIC LOCATION & INSTITUTION */
                <>
                  <div>
                    <label className="block text-xs font-bold text-slate-800 mb-1">
                      {domain.locationLabel} <span className="text-rose-500">*</span>
                    </label>
                    <p className="text-[11px] text-slate-500 mb-2">
                      {domain.locationDescription}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {COUNTRY_OPTIONS.map((c) => {
                        const isSelected = targetCountry === c.id
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => handleCountrySelect(c.id)}
                            className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                              isSelected
                                ? 'bg-teal-50/80 border-[#0EA5A4] text-[#0F766E] shadow-2xs ring-1 ring-[#0EA5A4]'
                                : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                            }`}
                          >
                            <p className="font-bold text-xs">{c.label}</p>
                            <p className="text-[10px] text-slate-400 mt-0.5">Mata uang: {c.currency}</p>
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  <div className="pt-2 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-slate-800 mb-1">
                        Kota / Wilayah Tujuan (Opsional)
                      </label>
                      <input
                        type="text"
                        value={targetCity}
                        onChange={(e) => setTargetCity(e.target.value)}
                        placeholder="Contoh: Bandung, Yogyakarta, Melbourne"
                        className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0EA5A4] focus:border-transparent"
                      />
                      <p className="text-[10px] text-slate-400 mt-1">
                        Boleh dikosongkan jika belum ada preferensi kota spesifik.
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-800 mb-1">
                        Tipe Institusi
                      </label>
                      <select
                        value={institutionType}
                        onChange={(e) => setInstitutionType(e.target.value)}
                        className="w-full px-3 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-[#0EA5A4] focus:border-transparent"
                      >
                        {INSTITUTION_TYPES.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-slate-100">
                    <label className="block text-xs font-bold text-slate-800 mb-1">
                      Nama Sekolah / Perguruan Tinggi Sasaran (Opsional)
                    </label>
                    <input
                      type="text"
                      value={institutionName}
                      onChange={(e) => setInstitutionName(e.target.value)}
                      placeholder="Contoh: Universitas Indonesia, ITB, NUS (boleh kosong)"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0EA5A4] focus:border-transparent"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">
                      Belum tahu kampusnya? Tidak masalah. Estimasi biaya tetap dapat dihitung berdasarkan jenjang dan wilayah.
                    </p>
                  </div>
                </>
              ) : (
                /* NON-EDUCATION DOMAIN-SPECIFIC LOCATION */
                <>
                  <div>
                    <h3 className="text-xs font-bold text-slate-900 mb-1">{domain.locationTitle}</h3>
                    <label className="block text-xs font-bold text-slate-800 mb-1">
                      {domain.locationLabel} <span className="text-rose-500">*</span>
                    </label>
                    <p className="text-[11px] text-slate-500 mb-2">
                      {domain.locationDescription}
                    </p>

                    {/* Quick chips for popular cities/destinations */}
                    {domain.commonCities && domain.commonCities.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2.5">
                        {domain.commonCities.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setTargetCity(c)}
                            className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all cursor-pointer ${
                              targetCity === c
                                ? 'bg-teal-700 text-white font-semibold shadow-2xs'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200/80'
                            }`}
                          >
                            {c}
                          </button>
                        ))}
                      </div>
                    )}

                    <input
                      type="text"
                      value={targetCity}
                      onChange={(e) => setTargetCity(e.target.value)}
                      placeholder={domain.locationPlaceholder}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0EA5A4] focus:border-transparent"
                    />
                  </div>

                  <div className="pt-2 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-slate-800 mb-1">
                        Negara / Wilayah Cakupan
                      </label>
                      <select
                        value={targetCountry}
                        onChange={(e) => handleCountrySelect(e.target.value)}
                        className="w-full px-3 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-[#0EA5A4] focus:border-transparent"
                      >
                        {COUNTRY_OPTIONS.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.label} ({c.currency})
                          </option>
                        ))}
                      </select>
                      <p className="text-[10px] text-slate-400 mt-1">
                        Standar acuan mata uang dan estimasi biaya.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* STEP 3: Waktu & Durasi */}
          {currentStep === 3 && (
            <div className="space-y-4 animate-in fade-in duration-150">
              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1">
                  {domain.targetYearLabel} <span className="text-rose-500">*</span>
                </label>
                <p className="text-[11px] text-slate-500 mb-2">
                  {domain.targetYearDescription}
                </p>

                {/* Quick year shortcuts */}
                <div className="flex flex-wrap gap-1.5 mb-2.5">
                  {[1, 2, 3, 5, 8, 10].map((offset) => {
                    const yr = new Date().getFullYear() + offset
                    return (
                      <button
                        key={yr}
                        type="button"
                        onClick={() => setTargetYear(yr)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                          targetYear === yr
                            ? 'bg-teal-700 text-white shadow-2xs'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        Tahun {yr} (+{offset} thn)
                      </button>
                    )
                  })}
                </div>

                <div className="max-w-xs">
                  <input
                    type="number"
                    min="2025"
                    max="2060"
                    value={targetYear || ''}
                    onChange={(e) => setTargetYear(Number(e.target.value))}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0EA5A4] focus:border-transparent"
                  />
                </div>
              </div>

              <div className="pt-2 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">
                    {domain.durationLabel}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="360"
                      value={durationMonths || 12}
                      onChange={(e) => setDurationMonths(Number(e.target.value))}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0EA5A4] focus:border-transparent"
                    />
                    <span className="text-xs text-slate-500 flex-shrink-0">
                      {durationMonths >= 12
                        ? `~${(durationMonths / 12).toFixed(1)} tahun`
                        : `~${durationMonths} bulan`}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">
                    {domain.durationDescription}
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">
                    Mata Uang Acuan
                  </label>
                  <select
                    value={targetCurrency}
                    onChange={(e) => setTargetCurrency(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-[#0EA5A4] focus:border-transparent"
                  >
                    <option value="IDR">IDR - Rupiah Indonesia</option>
                    <option value="SGD">SGD - Dolar Singapura</option>
                    <option value="AUD">AUD - Dolar Australia</option>
                    <option value="USD">USD - Dolar AS</option>
                    <option value="GBP">GBP - Poundsterling Inggris</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="MYR">MYR - Ringgit Malaysia</option>
                    <option value="JPY">JPY - Yen Jepang</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: Ringkasan & Kesiapan (Readiness) */}
          {currentStep === 4 && (
            <div className="space-y-4 animate-in fade-in duration-150">
              {/* Readiness Status Box */}
              <div
                className={`p-4 rounded-2xl border ${
                  readiness.isComplete
                    ? 'bg-emerald-50/70 border-emerald-200 text-emerald-950'
                    : 'bg-amber-50/70 border-amber-200 text-amber-950'
                }`}
              >
                <div className="flex items-start gap-2.5">
                  {readiness.isComplete ? (
                    <CheckCircle2 size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <h4 className="font-bold text-xs sm:text-sm">
                      {readiness.isComplete
                        ? 'Spesifikasi Sasaran Siap Dihitung (P4 Ready)'
                        : `Spesifikasi Belum Lengkap (${readiness.completedFieldsCount}/4 Data Kunci)`}
                    </h4>
                    <p className="text-[11px] text-slate-600 mt-1 leading-relaxed">
                      {readiness.isComplete
                        ? `Semua 4 parameter kunci (${domain.readinessKeyParameters}) telah terisi secara terstruktur. BekalNanti dapat memproyeksikan biaya pada tahap selanjutnya.`
                        : 'Kamu tetap dapat menyimpan data ini sebagai draf. Untuk melanjutkan ke kalkulasi biaya, lengkapi data kunci yang masih kurang.'}
                    </p>
                  </div>
                </div>

                {!readiness.isComplete && readiness.missingFields.length > 0 && (
                  <div className="mt-3 pt-2.5 border-t border-amber-200/60 text-xs">
                    <span className="font-bold text-amber-800 text-[11px] block mb-1">
                      Data yang masih dibutuhkan:
                    </span>
                    <ul className="list-disc list-inside space-y-0.5 text-[11px] text-amber-900">
                      {readiness.missingFields.map((f, idx) => (
                        <li key={idx}>{f}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Summary Specs Grid */}
              <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 text-xs space-y-2.5">
                <span className="text-[11px] font-bold text-slate-700 block uppercase tracking-wider">
                  Rincian Spesifikasi Terpetakan
                </span>
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div className="p-2 rounded-lg bg-white border border-slate-200">
                    <span className="text-slate-400 block text-[10px]">Tujuan Bekal</span>
                    <span className="font-bold text-slate-800">{activeGoal.name}</span>
                  </div>
                  <div className="p-2 rounded-lg bg-white border border-slate-200">
                    <span className="text-slate-400 block text-[10px]">{domain.targetStageLabel}</span>
                    <span className="font-bold text-slate-800">
                      {domain.stages.find((s) => s.id === targetStage)?.label || targetStage || '-'}
                    </span>
                  </div>
                  <div className="p-2 rounded-lg bg-white border border-slate-200">
                    <span className="text-slate-400 block text-[10px]">{domain.programNameLabel}</span>
                    <span className="font-bold text-slate-800">{programName || '-'}</span>
                  </div>
                  <div className="p-2 rounded-lg bg-white border border-slate-200">
                    <span className="text-slate-400 block text-[10px]">{domain.locationLabel}</span>
                    <span className="font-bold text-slate-800">
                      {targetCity ? (targetCountry && targetCountry !== 'Indonesia' ? `${targetCity}, ${targetCountry}` : targetCity) : (targetCountry || '-')}
                    </span>
                  </div>
                  <div className="p-2 rounded-lg bg-white border border-slate-200">
                    <span className="text-slate-400 block text-[10px]">{domain.targetYearLabel}</span>
                    <span className="font-bold text-slate-800">
                      {targetYear ? `Tahun ${targetYear}` : '-'}
                    </span>
                  </div>
                  <div className="p-2 rounded-lg bg-white border border-slate-200">
                    <span className="text-slate-400 block text-[10px]">{domain.durationLabel}</span>
                    <span className="font-bold text-slate-800">
                      {durationMonths} bulan ({targetCurrency})
                    </span>
                  </div>
                </div>

                {domain.showInstitution && institutionName && (
                  <div className="p-2 rounded-lg bg-white border border-slate-200 text-[11px]">
                    <span className="text-slate-400 block text-[10px]">Institusi Pilihan</span>
                    <span className="font-bold text-slate-800">{institutionName}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Feedback & Error Alerts */}
          {errorMessage && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2">
              <AlertCircle size={14} className="flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {saveSuccess && (
            <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs flex items-center gap-2">
              <CheckCircle2 size={14} className="flex-shrink-0" />
              <span>Spesifikasi sasaran berhasil disimpan.</span>
            </div>
          )}
        </div>

        {/* Modal Footer Controls */}
        <div className="p-4 sm:p-5 border-t border-slate-200 bg-slate-50/70 flex items-center justify-between gap-2">
          {/* Left: Back / Close */}
          {currentStep > 1 ? (
            <button
              type="button"
              onClick={() => setCurrentStep((s) => s - 1)}
              className="px-3.5 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 text-xs font-semibold flex items-center gap-1 transition-colors cursor-pointer"
            >
              <ChevronLeft size={14} />
              <span>Kembali</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-2 rounded-xl text-slate-500 hover:text-slate-800 text-xs font-semibold transition-colors cursor-pointer"
            >
              Tutup
            </button>
          )}

          {/* Right: Save Draft / Next / Save & Close */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => handleSave(false)}
              className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              <span>Simpan Draf</span>
            </button>

            {currentStep < 4 ? (
              <button
                type="button"
                onClick={() => setCurrentStep((s) => s + 1)}
                className="px-4 py-2 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-xs font-bold flex items-center gap-1 transition-colors shadow-2xs cursor-pointer"
              >
                <span>Lanjut</span>
                <ChevronRight size={14} />
              </button>
            ) : (
              <button
                type="button"
                disabled={saving}
                onClick={() => handleSave(true)}
                className="px-4 py-2 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-xs font-bold flex items-center gap-1.5 transition-colors shadow-2xs cursor-pointer disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <CheckCircle2 size={13} />
                )}
                <span>Simpan &amp; Selesai</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
